/**
 * MemosPage (iter126.1)
 *
 * Three-tab memos hub: Contact Memos · Personal Memos · Team Memos.
 * Memo creation lives in PopupSheet popups (mobile = bottom sheet,
 * desktop = bottom-right corner card) modeled on the new-email
 * composer.
 *
 * Header `+` button is dynamic:
 *   • personal tab → "+ New memo"
 *   • team tab     → "+ New memo" + "+ New team"
 *   • contacts tab → no header action (use the in-list `+ Memo`)
 *
 * @-mentions in team memos: typing `@` opens a teammate picker.
 * Inserts a `@[Full Name](user_id)` token (rendered as a pill in
 * the editor). On save the backend fans out a "memo_mention"
 * notification to every tagged user.
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Sheet, SheetContent, SheetTitle } from '../components/ui/sheet';
import { Dialog, DialogContent, DialogTitle } from '../components/ui/dialog';
import { Plus, ArrowLeft, Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, IndentIncrease, IndentDecrease, CheckSquare, Highlighter, Paperclip, Save, Users as UsersIcon, X, Star, UserPlus, Globe, Search, Eye, Pencil, Trash2, Info } from 'lucide-react';
import PageLoader from '../components/PageLoader';
import { toast } from 'sonner';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePageHeader } from '../context/PageHeaderContext';

const cls = (...xs) => xs.filter(Boolean).join(' ');
const initials = (name) => ((name || '').trim().split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase()).join('') || '?');
const when = (iso) => {
  if (!iso) return '';
  const d = new Date(iso); if (Number.isNaN(d.getTime())) return '';
  const t = (Date.now() - d.getTime()) / 1000;
  if (t < 60) return 'just now';
  if (t < 3600) return `${Math.round(t / 60)}m`;
  if (t < 86400) return `${Math.round(t / 3600)}h`;
  if (t < 604800) return `${Math.round(t / 86400)}d`;
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

// ─────────── PopupSheet: bottom-sheet on mobile, bottom-right card on desktop ─────────

const useDesktop = () => {
  const [isDesk, setIsDesk] = useState(() => typeof window !== 'undefined' && window.matchMedia('(min-width: 768px) and (orientation: landscape)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px) and (orientation: landscape)');
    const handler = (e) => setIsDesk(e.matches);
    mq.addEventListener?.('change', handler) || mq.addListener?.(handler);
    return () => { mq.removeEventListener?.('change', handler) || mq.removeListener?.(handler); };
  }, []);
  return isDesk;
};

// Detect whether the app is running inside the ORB embed iframe.
// When in the embed, the bottom nav shows on desktop too (see
// Layout.js) — so the desktop PopupSheet has to (a) lift its
// z-index above the nav's z-50 and (b) push its `bottom` offset
// up by the nav's height so it doesn't sit half-hidden behind it.
const useIsOrbEmbed = () => {
  const [embed] = useState(() => {
    try { return window.self !== window.top; } catch { return false; }
  });
  return embed;
};

const PopupSheet = ({ open, onClose, title, children, testId, width = 480 }) => {
  const isDesk = useDesktop();
  const isOrbEmbed = useIsOrbEmbed();
  if (!open) return null;
  if (isDesk) {
    // ORB embed: clear the bottom nav (h-16 = 64px) + the original
    // 24px breathing-room gap = 88px.
    const bottomOffset = isOrbEmbed ? 88 : 24;
    return (
      <>
        <div className="fixed inset-0 z-[60]" onClick={onClose} />
        <div
          className="fixed z-[61] flex flex-col rounded-2xl shadow-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#1f1f1f] overflow-hidden"
          style={{ right: '24px', bottom: `${bottomOffset}px`, width, maxHeight: '78vh' }}
          onClick={(e) => e.stopPropagation()}
          data-testid={testId}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-black/5 dark:border-white/5 flex-shrink-0">
            <p className="font-semibold text-sm">{title}</p>
            <button onClick={onClose} className="p-1.5 rounded-full hover:bg-muted"><X className="w-4 h-4" /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-3">{children}</div>
        </div>
      </>
    );
  }
  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="bottom" className="h-[83vh] p-0 flex flex-col rounded-t-3xl border-t-0 bg-white dark:bg-[#1f1f1f] [&>button.absolute]:hidden" data-testid={testId}>
        <SheetTitle className="sr-only">{title}</SheetTitle>
        <div className="flex items-center justify-between px-4 py-3 border-b border-black/5 dark:border-white/5 flex-shrink-0">
          <p className="font-semibold text-sm">{title}</p>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-muted"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-3">{children}</div>
      </SheetContent>
    </Sheet>
  );
};

// ─────────── Mention-aware rich editor ─────────

const RichEditor = ({ value, onChange, placeholder = 'Start writing…', testId, enableMentions, mentionUsers = [] }) => {
  const ref = useRef(null);
  const fileRef = useRef(null);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQ, setMentionQ] = useState('');
  const [mentionAnchor, setMentionAnchor] = useState(null);
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || '')) ref.current.innerHTML = value || '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const exec = (cmd, arg) => {
    document.execCommand(cmd, false, arg);
    ref.current?.focus();
    onChange(ref.current?.innerHTML || '');
  };
  const onInput = (e) => {
    onChange(e.currentTarget.innerHTML || '');
    if (!enableMentions) return;
    // Detect a freshly-typed `@` followed by characters and pop the picker
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const rng = sel.getRangeAt(0);
    const text = rng.startContainer.nodeValue || '';
    const idx = text.lastIndexOf('@', rng.startOffset - 1);
    if (idx >= 0 && rng.startOffset > idx) {
      const fragment = text.slice(idx + 1, rng.startOffset);
      if (/^[\w\s]*$/.test(fragment) && fragment.length <= 30) {
        setMentionQ(fragment.toLowerCase());
        setMentionOpen(true);
        setMentionAnchor({ node: rng.startContainer, start: idx, end: rng.startOffset });
        return;
      }
    }
    setMentionOpen(false);
  };
  const insertMention = (u) => {
    if (!mentionAnchor) return;
    const { node, start, end } = mentionAnchor;
    const range = document.createRange();
    range.setStart(node, start); range.setEnd(node, end);
    range.deleteContents();
    // Insert a non-editable mention pill. At post-time the composer
    // converts these pills into the backend's `@[Name](user_id)`
    // token format. We also drop a trailing space so typing
    // continues naturally after the pill.
    const pill = document.createElement('span');
    pill.className = 'mention-pill';
    pill.contentEditable = 'false';
    pill.dataset.userId = u.id;
    pill.textContent = `@${u.name || u.email}`;
    range.insertNode(pill);
    const space = document.createTextNode('\u00a0');
    pill.after(space);
    // Move caret after the inserted pill+space.
    const sel = window.getSelection();
    sel.removeAllRanges();
    const after = document.createRange();
    after.setStartAfter(space); after.collapse(true);
    sel.addRange(after);
    setMentionOpen(false);
    onChange(ref.current?.innerHTML || '');
  };
  const insertChecklistItem = () => {
    document.execCommand('insertHTML', false, `<div class="memo-task"><input type="checkbox" data-task-checkbox><span contenteditable="true"></span></div><div><br></div>`);
    onChange(ref.current?.innerHTML || '');
  };
  const attach = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = () => {
      if (f.type.startsWith('image/')) {
        document.execCommand('insertHTML', false, `<img src="${r.result}" alt="${f.name}" style="max-width:240px;border-radius:8px;margin:6px 0" />`);
      } else {
        document.execCommand('insertHTML', false, `<a href="${r.result}" download="${f.name}" class="text-[#586D52] underline">📎 ${f.name}</a> `);
      }
      onChange(ref.current?.innerHTML || '');
    };
    r.readAsDataURL(f);
    e.target.value = '';
  };
  const filtered = useMemo(() => (mentionUsers || []).filter((u) => (u.name || u.email || '').toLowerCase().includes(mentionQ)).slice(0, 8), [mentionUsers, mentionQ]);
  return (
    <div className="space-y-2 relative">
      <div className="flex flex-wrap items-center gap-1 p-2 rounded-xl border border-black/10 dark:border-white/10 bg-[#586D52]/5">
        <button type="button" onClick={() => exec('bold')} className="p-1.5 rounded hover:bg-[#586D52]/15" title="Bold" data-testid="memo-fmt-bold"><Bold className="w-4 h-4" /></button>
        <button type="button" onClick={() => exec('italic')} className="p-1.5 rounded hover:bg-[#586D52]/15" title="Italic" data-testid="memo-fmt-italic"><Italic className="w-4 h-4" /></button>
        <button type="button" onClick={() => exec('underline')} className="p-1.5 rounded hover:bg-[#586D52]/15" title="Underline" data-testid="memo-fmt-underline"><UnderlineIcon className="w-4 h-4" /></button>
        <button type="button" onClick={() => exec('hiliteColor', '#fef08a')} className="p-1.5 rounded hover:bg-[#586D52]/15" title="Highlight" data-testid="memo-fmt-highlight"><Highlighter className="w-4 h-4" /></button>
        <span className="w-px h-5 bg-black/10 dark:bg-white/10 mx-1" />
        <button type="button" onClick={() => exec('insertUnorderedList')} className="p-1.5 rounded hover:bg-[#586D52]/15" title="Bulleted list" data-testid="memo-fmt-ul"><List className="w-4 h-4" /></button>
        <button type="button" onClick={() => exec('insertOrderedList')} className="p-1.5 rounded hover:bg-[#586D52]/15" title="Numbered list" data-testid="memo-fmt-ol"><ListOrdered className="w-4 h-4" /></button>
        <button type="button" onClick={() => exec('indent')} className="p-1.5 rounded hover:bg-[#586D52]/15" title="Indent" data-testid="memo-fmt-indent"><IndentIncrease className="w-4 h-4" /></button>
        <button type="button" onClick={() => exec('outdent')} className="p-1.5 rounded hover:bg-[#586D52]/15" title="Outdent" data-testid="memo-fmt-outdent"><IndentDecrease className="w-4 h-4" /></button>
        <button type="button" onClick={insertChecklistItem} className="p-1.5 rounded hover:bg-[#586D52]/15" title="Checkbox item" data-testid="memo-fmt-checkbox"><CheckSquare className="w-4 h-4" /></button>
        <button type="button" onClick={() => fileRef.current?.click()} className="p-1.5 rounded hover:bg-[#586D52]/15" title="Attach" data-testid="memo-fmt-attach"><Paperclip className="w-4 h-4" /></button>
        <input ref={fileRef} type="file" className="hidden" onChange={attach} data-testid="memo-attach-input" />
      </div>
      <div
        ref={ref}
        contentEditable
        onInput={onInput}
        onClick={(e) => {
          // Toggle a checkbox tap-target inside the editor.
          if (e.target.tagName === 'INPUT' && e.target.type === 'checkbox') {
            e.target.checked = !e.target.checked;
            onChange(ref.current?.innerHTML || '');
          }
        }}
        suppressContentEditableWarning
        className="min-h-[260px] max-h-[55vh] overflow-y-auto p-3 rounded-xl border border-[#586D52]/40 bg-transparent text-sm focus:outline-none focus:ring-0 prose prose-sm dark:prose-invert max-w-none"
        data-placeholder={placeholder}
        data-testid={testId}
      />
      {enableMentions && mentionOpen && filtered.length > 0 && (
        <div className="absolute z-20 mt-1 left-3 right-3 bg-white dark:bg-[#1f1f1f] rounded-xl border border-black/10 dark:border-white/10 shadow-lg max-h-60 overflow-y-auto" data-testid="memo-mention-popover">
          {filtered.map((u) => (
            <button key={u.id} onClick={() => insertMention(u)} className="w-full flex items-center gap-2 p-2 text-left hover:bg-muted text-sm" data-testid={`memo-mention-${u.id}`}>
              <Avatar className="h-7 w-7">
                {u.profile_picture ? <AvatarImage src={u.profile_picture} alt="" /> : null}
                <AvatarFallback className="bg-[#586D52]/20 text-[#586D52] text-xs">{initials(u.name || u.email || '?')}</AvatarFallback>
              </Avatar>
              <span className="flex-1 truncate">{u.name || u.email}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ─────────── Composers ─────────

const ContactMemoComposer = ({ open, onClose, contact, api, onPosted }) => {
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setBody(''); }, [open]);
  const post = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      await api.post(`/memos/contacts/${contact.id}`, { body: body.trim() });
      onClose(); onPosted?.();
    } catch (e) { toast.error('Failed to post memo'); }
    finally { setBusy(false); }
  };
  return (
    <PopupSheet open={open} onClose={onClose} title={`New memo · ${contact?.name || ''}`} testId="memo-contact-composer">
      <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Write what you want to remember about this contact…" className="w-full min-h-[200px] p-3 rounded-xl border border-[#586D52]/40 bg-transparent text-sm resize-none outline-none focus:ring-0" data-testid="memo-body-input" />
      <div className="mt-3 flex justify-end">
        <Button onClick={post} disabled={busy || !body.trim()} className="bg-[#586D52] hover:bg-[#4a5d46]" data-testid="memo-submit"><Save className="w-4 h-4 mr-1.5" /> Save memo</Button>
      </div>
    </PopupSheet>
  );
};

const PersonalMemoComposer = ({ open, onClose, api, editing, onSaved }) => {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setTitle(editing?.title || '');
    setBody(editing?.body || '');
  }, [open, editing]);
  const save = async () => {
    setBusy(true);
    try {
      const payload = { title, body };
      if (editing?.id) await api.put(`/memos/personal/${editing.id}`, payload);
      else await api.post('/memos/personal', payload);
      onClose(); onSaved?.();
    } catch (e) { toast.error('Failed to save memo'); }
    finally { setBusy(false); }
  };
  return (
    <PopupSheet open={open} onClose={onClose} title={editing ? 'Edit memo' : 'New memo'} testId="memo-personal-composer" width={540}>
      <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" className="text-base font-semibold border-0 px-0 focus-visible:ring-0 bg-transparent mb-2" data-testid="memo-title-input" />
      <RichEditor value={body} onChange={setBody} testId="memo-rich-body" />
      <div className="mt-3 flex justify-end gap-2">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={save} disabled={busy} className="bg-[#586D52] hover:bg-[#4a5d46]" data-testid="memo-save"><Save className="w-4 h-4 mr-1.5" /> Save</Button>
      </div>
    </PopupSheet>
  );
};

const TeamMemoComposer = ({ open, onClose, api, team, mentionUsers, focusMemoId, onPosted }) => {
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setBody(''); }, [open]);
  const post = async () => {
    // Convert mention pills (visible `@Name` chips) back into the
    // backend-parseable token `@[Name](user_id)` so the server can
    // fan out notifications. The plain typed text is preserved.
    const wrap = document.createElement('div');
    wrap.innerHTML = body || '';
    wrap.querySelectorAll('.mention-pill').forEach((el) => {
      const id = el.dataset.userId || '';
      const name = (el.textContent || '').replace(/^@/, '');
      el.replaceWith(document.createTextNode(`@[${name}](${id})`));
    });
    // Strip any remaining HTML tags (lists, attachments etc — for
    // the simple team memo we store plain text + tokens).
    const text = (wrap.textContent || '').replace(/[ \t]+/g, ' ').replace(/\n{3,}/g, '\n\n').trim();
    if (!text) return;
    setBusy(true);
    try {
      await api.post(`/memos/teams/${team.id}/memos`, { body: text });
      onClose(); onPosted?.();
    } catch (e) { toast.error('Failed to post memo'); }
    finally { setBusy(false); }
  };
  return (
    <PopupSheet open={open} onClose={onClose} title={`Post to ${team?.name || ''}`} testId="memo-team-composer" width={540}>
      <p className="text-[11px] text-muted-foreground mb-1">Tip: type <code>@</code> to tag a teammate. They'll get a notification.</p>
      <RichEditor value={body} onChange={setBody} testId="memo-team-rich" enableMentions mentionUsers={mentionUsers || []} />
      <div className="mt-3 flex justify-end">
        <Button onClick={post} disabled={busy} className="bg-[#586D52] hover:bg-[#4a5d46]" data-testid="memo-team-submit"><Save className="w-4 h-4 mr-1.5" /> Post</Button>
      </div>
    </PopupSheet>
  );
};

const CreateTeamPopup = ({ open, onClose, api, onCreated }) => {
  const [name, setName] = useState('');
  const [internal, setInternal] = useState([]);
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setName(''); setPicked(new Set());
    (async () => {
      try {
        const { data } = await api.get('/contacts');
        setInternal((data?.internal || []).filter((u) => u.id));
      } catch (e) { setInternal([]); }
    })();
  }, [open, api]);
  const submit = async () => {
    if (!name.trim() || picked.size === 0) return;
    setBusy(true);
    try {
      await api.post('/memos/teams', { name: name.trim(), member_ids: [...picked] });
      toast.success('Team created · invites sent');
      onClose(); onCreated?.();
    } catch (e) { toast.error('Failed to create team'); }
    finally { setBusy(false); }
  };
  return (
    <PopupSheet open={open} onClose={onClose} title="New memo team" testId="memo-new-team-popup" width={420}>
      <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Team name" className="mb-2" data-testid="memo-new-team-name" />
      <p className="text-xs text-muted-foreground mb-2">Invite teammates from your internal contacts. They'll get an invitation popup.</p>
      <div className="max-h-[40vh] overflow-y-auto space-y-1">
        {internal.map((u) => {
          const on = picked.has(u.id);
          return (
            <button key={u.id} onClick={() => setPicked((s) => { const n = new Set(s); if (on) n.delete(u.id); else n.add(u.id); return n; })} className={cls('w-full flex items-center gap-2 p-2 rounded-lg text-sm text-left', on ? 'bg-[#586D52]/15' : 'hover:bg-muted')} data-testid={`memo-new-team-pick-${u.id}`}>
              <Avatar className="h-7 w-7">{u.profile_picture ? <AvatarImage src={u.profile_picture} alt="" /> : null}<AvatarFallback className="bg-[#586D52]/20 text-[#586D52] text-xs">{initials(u.name || u.email || '?')}</AvatarFallback></Avatar>
              <span className="flex-1 truncate">{u.name || u.email}</span>
              {on && <span className="text-[10px] text-[#586D52] font-semibold">PICKED</span>}
            </button>
          );
        })}
        {internal.length === 0 && <p className="text-center text-xs text-muted-foreground py-4">No internal contacts found.</p>}
      </div>
      <div className="flex justify-end gap-2 mt-2">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={submit} disabled={busy || !name.trim() || picked.size === 0} className="bg-[#586D52] hover:bg-[#4a5d46]" data-testid="memo-new-team-create">Create</Button>
      </div>
    </PopupSheet>
  );
};

// ─────────── Tab content ─────────

const ContactMemosTab = ({ api, openComposerForContact, search }) => {
  const [items, setItems] = useState(null);
  const [picked, setPicked] = useState(null);
  const [thread, setThread] = useState(null);
  // Edit / delete dialogs — operate on the memo currently in flight.
  const [editTarget, setEditTarget] = useState(null);
  const [editBody, setEditBody] = useState('');
  const [editRationale, setEditRationale] = useState('');
  const [editBusy, setEditBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteRationale, setDeleteRationale] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get('/memos/contacts'); setItems(data || []); }
    catch (e) { setItems([]); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const loadThread = useCallback(async (id) => {
    if (!id) { toast.error('Missing contact id'); return; }
    try { const { data } = await api.get(`/memos/contacts/${id}`); setThread(data); }
    catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'unknown error';
      toast.error(`Failed to load memos: ${detail}`);
    }
  }, [api]);

  const openEdit = (m) => {
    setEditTarget(m);
    setEditBody(m.body || '');
    setEditRationale('');
  };
  const submitEdit = async () => {
    if (!editTarget) return;
    if (!editRationale.trim() || editRationale.trim().length < 3) {
      toast.error('Please provide a rationale (3+ characters).');
      return;
    }
    setEditBusy(true);
    try {
      await api.put(`/memos/contacts/memo/${editTarget.id}`, {
        body: editBody,
        rationale: editRationale.trim(),
      });
      toast.success('Memo updated');
      setEditTarget(null);
      if (picked?.id) await loadThread(picked.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Edit failed');
    } finally { setEditBusy(false); }
  };
  const submitDelete = async () => {
    if (!deleteTarget) return;
    if (!deleteRationale.trim() || deleteRationale.trim().length < 3) {
      toast.error('Please provide a rationale (3+ characters).');
      return;
    }
    setDeleteBusy(true);
    try {
      await api.delete(`/memos/contacts/memo/${deleteTarget.id}`, {
        data: { rationale: deleteRationale.trim() },
      });
      toast.success('Memo deleted');
      setDeleteTarget(null);
      setDeleteRationale('');
      if (picked?.id) await loadThread(picked.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    } finally { setDeleteBusy(false); }
  };

  if (items === null) return <PageLoader label="Loading contacts..." fullScreen={false} gradient={false} />;
  if (picked && thread) {
    const c = thread.contact;
    const canModerate = !!thread.viewer?.can_moderate;
    const isAdminViewer = !!thread.viewer?.is_admin;
    return (
      <div className="space-y-3" data-testid="memo-contact-thread">
        <button onClick={() => { setPicked(null); setThread(null); }} className="inline-flex items-center gap-1 text-sm text-[#586D52]"><ArrowLeft className="w-4 h-4" /> Back</button>
        {/* iter145 — Contact header + memo chain live INSIDE the SAME
            rounded card so the whole contact reads as ONE big email-style
            thread (contact = subject line, memos = chronological entries).
            Per user feedback in iter144 (screenshot showed the header and
            the entries in two visually separate cards). */}
        <div className="rounded-2xl bg-white dark:bg-[#141414] border border-black/10 dark:border-white/10 shadow-sm overflow-hidden" data-testid="memo-contact-chain">
          {/* Contact "subject line" header */}
          <div className="flex items-center gap-3 p-4 border-b border-black/10 dark:border-white/10 bg-[#586D52]/5 dark:bg-[#586D52]/10">
            <Avatar className="h-12 w-12">{c.profile_picture ? <AvatarImage src={c.profile_picture} alt="" /> : null}<AvatarFallback className="bg-[#757150]/20 text-[#757150] text-base font-semibold">{initials(c.name)}</AvatarFallback></Avatar>
            <div className="min-w-0 flex-1">
              <p className="text-base font-semibold truncate">{c.name}</p>
              <p className="text-xs text-muted-foreground truncate">{[c.company, c.title].filter(Boolean).join(', ') || c.phone || c.email}</p>
            </div>
            <Button size="sm" className="bg-[#586D52] hover:bg-[#4a5d46]" onClick={() => openComposerForContact(c, () => loadThread(c.id))} data-testid="memo-add-btn"><Plus className="w-4 h-4 mr-1" /> Memo</Button>
          </div>
          {/* Memo entries — chronological, oldest → newest */}
          {(thread.memos || []).length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-8 px-4">No memos yet — click "Memo" to add one.</p>
          ) : (
            <div className="p-4">
              {(thread.memos || []).map((m, idx) => (
                <div
                  key={m.id}
                  className={cls(idx > 0 && 'mt-4 pt-4 border-t border-dashed border-black/10 dark:border-white/10')}
                  data-testid={`memo-row-${m.id}`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <p className="text-xs font-semibold text-[#586D52]">
                      {m.owner_name}
                      {m.source === 'signup' && <span className="ml-1 text-muted-foreground font-normal">(signup form)</span>}
                    </p>
                    <div className="flex items-center gap-2">
                      <p className="text-[11px] text-muted-foreground">{new Date(m.created_at).toLocaleString()}</p>
                      {canModerate && !m.is_deleted && (
                        <>
                          <button
                            onClick={() => openEdit(m)}
                            className="p-1 rounded hover:bg-muted text-muted-foreground"
                            aria-label="Edit memo"
                            data-testid={`memo-edit-btn-${m.id}`}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => { setDeleteTarget(m); setDeleteRationale(''); }}
                            className="p-1 rounded hover:bg-muted text-red-500"
                            aria-label="Delete memo"
                            data-testid={`memo-delete-btn-${m.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  {m.is_deleted ? (
                    <p className="text-sm italic text-muted-foreground" data-testid={`memo-deleted-${m.id}`}>
                      [Memo deleted]
                      {isAdminViewer && m.body ? (
                        <span className="block mt-1 text-xs whitespace-pre-wrap break-words text-foreground/70">Original: “{m.body}”</span>
                      ) : null}
                    </p>
                  ) : (
                    <p className="text-sm whitespace-pre-wrap break-words">{m.body}</p>
                  )}
                  {m.is_edited && !m.is_deleted && (
                    <p className="mt-1 text-[11px] italic text-muted-foreground" data-testid={`memo-edited-label-${m.id}`}>
                      Edited by {m.edited_label} on {new Date(m.edited_at).toLocaleString()}
                      {isAdminViewer && m.edit_rationale ? (
                        <span className="block text-[11px] not-italic text-foreground/70">Rationale: {m.edit_rationale}</span>
                      ) : null}
                    </p>
                  )}
                  {m.is_deleted && (
                    <p className="mt-1 text-[11px] italic text-muted-foreground" data-testid={`memo-deleted-label-${m.id}`}>
                      Deleted by {m.deleted_label} on {new Date(m.deleted_at).toLocaleString()}
                      {isAdminViewer && m.delete_rationale ? (
                        <span className="block text-[11px] not-italic text-foreground/70">Rationale: {m.delete_rationale}</span>
                      ) : null}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Edit dialog */}
        <Dialog open={!!editTarget} onOpenChange={(v) => { if (!v) setEditTarget(null); }}>
          <DialogContent className="bg-white dark:bg-[#1f1f1f] rounded-2xl max-w-md" data-testid="memo-edit-dialog">
            <DialogTitle className="text-base font-semibold">Edit memo</DialogTitle>
            <p className="text-[11px] text-muted-foreground -mt-1">
              The original poster + timestamp stay intact. Your edit appends an "Edited by …" footer for everyone, and a rationale visible to admins.
            </p>
            <textarea
              value={editBody}
              onChange={(e) => setEditBody(e.target.value)}
              className="w-full min-h-[140px] p-3 rounded-xl border border-[#586D52]/40 bg-transparent text-sm resize-none outline-none focus:ring-0"
              data-testid="memo-edit-body"
            />
            <div>
              <p className="text-[11px] font-semibold text-muted-foreground mb-1 flex items-center gap-1"><Info className="w-3 h-3" /> Rationale (admin-visible)</p>
              <Input
                value={editRationale}
                onChange={(e) => setEditRationale(e.target.value)}
                placeholder="Why are you editing this entry?"
                className="text-sm"
                data-testid="memo-edit-rationale"
              />
            </div>
            <div className="flex justify-end gap-2 mt-2">
              <Button variant="outline" onClick={() => setEditTarget(null)} disabled={editBusy}>Cancel</Button>
              <Button onClick={submitEdit} disabled={editBusy} className="bg-[#586D52] hover:bg-[#4a5d46]" data-testid="memo-edit-save">
                <Save className="w-4 h-4 mr-1.5" /> Save edit
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete confirm + rationale dialog */}
        <Dialog open={!!deleteTarget} onOpenChange={(v) => { if (!v) { setDeleteTarget(null); setDeleteRationale(''); } }}>
          <DialogContent className="bg-white dark:bg-[#1f1f1f] rounded-2xl max-w-md" data-testid="memo-delete-dialog">
            <DialogTitle className="text-base font-semibold">Delete this memo?</DialogTitle>
            <p className="text-[11px] text-muted-foreground -mt-1">
              The memo body will be redacted in the thread but an audit row ("Deleted by …") stays so the history is preserved. Admins can still see the original body + your rationale.
            </p>
            <div>
              <p className="text-[11px] font-semibold text-muted-foreground mb-1 flex items-center gap-1"><Info className="w-3 h-3" /> Rationale (admin-visible)</p>
              <Input
                value={deleteRationale}
                onChange={(e) => setDeleteRationale(e.target.value)}
                placeholder="Why are you deleting this entry?"
                className="text-sm"
                data-testid="memo-delete-rationale"
              />
            </div>
            <div className="flex justify-end gap-2 mt-2">
              <Button variant="outline" onClick={() => { setDeleteTarget(null); setDeleteRationale(''); }} disabled={deleteBusy}>Cancel</Button>
              <Button onClick={submitDelete} disabled={deleteBusy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="memo-delete-confirm">
                <Trash2 className="w-4 h-4 mr-1.5" /> Delete
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }
  if (!items.length) return <p className="text-center text-sm text-muted-foreground py-12" data-testid="memo-contacts-empty">No external contacts yet. Add contacts from the Contacts page.</p>;
  const q = (search || '').trim().toLowerCase();
  const visible = !q ? items : items.filter((c) => (
    (c.name || '').toLowerCase().includes(q) ||
    (c.company || '').toLowerCase().includes(q) ||
    (c.title || '').toLowerCase().includes(q) ||
    (c.phone || '').toLowerCase().includes(q) ||
    (c.email || '').toLowerCase().includes(q) ||
    (c.last_memo_preview || '').toLowerCase().includes(q) ||
    (c.tags || []).join(' ').toLowerCase().includes(q)
  ));
  return (
    <div className="space-y-2" data-testid="memos-contact-list">
      {visible.length === 0 && <p className="text-center text-sm text-muted-foreground py-12">No contacts match that search.</p>}
      {visible.map((c) => (
        <button key={c.id} onClick={() => { setPicked(c); loadThread(c.id); }} className="flex items-start gap-3 px-3 py-3 rounded-2xl border border-black/10 dark:border-white/10 shadow-sm bg-white dark:bg-[#141414] text-left w-full" data-testid={`memo-contact-${c.id}`}>
          <Avatar className="h-10 w-10 mt-0.5 flex-shrink-0">{c.profile_picture ? <AvatarImage src={c.profile_picture} alt="" /> : null}<AvatarFallback className="bg-[#757150]/20 text-[#757150] font-semibold text-sm">{initials(c.name)}</AvatarFallback></Avatar>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="text-sm font-semibold truncate flex-1">{c.name || 'Unknown contact'}</p>
              <span className="text-[11px] text-muted-foreground flex-shrink-0">{when(c.last_memo_at)}</span>
            </div>
            <p className="text-xs text-muted-foreground truncate mt-0.5">{[c.company, c.title].filter(Boolean).join(', ') || c.phone || c.email}</p>
            <p className="text-xs text-foreground/80 truncate mt-1">{c.last_memo_preview || 'No memos yet'}</p>
          </div>
        </button>
      ))}
    </div>
  );
};

const PersonalMemosTab = ({ api, composerOpen, setComposerOpen, editing, setEditing, refreshKey, search }) => {
  const [memos, setMemos] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await api.get('/memos/personal'); setMemos(data || []); }
    catch (e) { setMemos([]); }
  }, [api]);
  useEffect(() => { load(); }, [load, refreshKey]);
  if (memos === null) return <PageLoader label="Loading memos..." fullScreen={false} gradient={false} />;
  const q = (search || '').trim().toLowerCase();
  const visible = !q ? memos : memos.filter((m) => (
    (m.title || '').toLowerCase().includes(q) ||
    (m.body || '').toLowerCase().includes(q) ||
    (m.tags || []).join(' ').toLowerCase().includes(q)
  ));
  return (
    <div className="space-y-2" data-testid="memos-personal-tab">
      {visible.length === 0 ? (
        <p className="text-center text-sm text-muted-foreground py-12">{q ? 'No memos match that search.' : <>No personal memos yet — use the <span className="font-semibold">+</span> in the header to add one.</>}</p>
      ) : visible.map((m) => (
        <button key={m.id} onClick={() => { setEditing(m); setComposerOpen(true); }} className="block w-full text-left p-3 rounded-2xl bg-white dark:bg-[#141414] border border-black/10 dark:border-white/10 shadow-sm" data-testid={`memo-personal-${m.id}`}>
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-semibold truncate flex-1">{m.title || '(untitled)'}</p>
            <p className="text-[11px] text-muted-foreground flex-shrink-0">{when(m.updated_at)}</p>
          </div>
          <div className="text-xs text-muted-foreground mt-1 line-clamp-2 prose prose-xs max-w-none dark:prose-invert" dangerouslySetInnerHTML={{ __html: m.body || '' }} />
        </button>
      ))}
      <PersonalMemoComposer open={composerOpen} onClose={() => setComposerOpen(false)} api={api} editing={editing} onSaved={load} />
    </div>
  );
};

const TeamMemosTab = ({ api, composerOpen, setComposerOpen, createTeamOpen, setCreateTeamOpen, focusTeamId, focusMemoId, refreshKey, search }) => {
  const [teams, setTeams] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [memos, setMemos] = useState([]);
  const [internalUsers, setInternalUsers] = useState([]);
  const [membersOpen, setMembersOpen] = useState(false);

  const loadTeams = useCallback(async () => {
    try {
      const { data } = await api.get('/memos/teams');
      setTeams(data || []);
      setActiveId((cur) => cur || focusTeamId || data?.[0]?.id || null);
    } catch (e) { setTeams([]); }
  }, [api, focusTeamId]);
  useEffect(() => { loadTeams(); }, [loadTeams, refreshKey]);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get('/contacts'); setInternalUsers((data?.internal || []).filter((u) => u.id)); }
      catch (e) { setInternalUsers([]); }
    })();
  }, [api]);
  const loadMemos = useCallback(async () => {
    if (!activeId) return;
    try { const { data } = await api.get(`/memos/teams/${activeId}/memos`); setMemos(data || []); }
    catch (e) { setMemos([]); }
  }, [api, activeId]);
  useEffect(() => { loadMemos(); }, [loadMemos]);

  const active = teams?.find((t) => t.id === activeId);

  // Resolve the active team's members. Prefer server-provided list,
  // fall back to cross-referencing with the internal users we pulled
  // for the @mention picker (which has profile pictures + industry).
  const activeMembers = useMemo(() => {
    if (!active) return [];
    const ids = active.member_ids || active.members?.map((m) => m.id || m) || [];
    return ids.map((id) => {
      const u = internalUsers.find((x) => x.id === id) || {};
      return {
        id,
        name: u.name || u.full_name || u.email || '(unknown)',
        role: u.role,
        industry: u.industry,
        industry_domain: u.industry_domain,
        profile_picture: u.profile_picture,
      };
    });
  }, [active, internalUsers]);

  // Listen for the header eye-icon click — opens the team-members
  // modal (the eye lives in the page header now, not on the page).
  useEffect(() => {
    const open = () => setMembersOpen(true);
    window.addEventListener('memos:open-team-members', open);
    return () => window.removeEventListener('memos:open-team-members', open);
  }, []);

  if (teams === null) return <PageLoader label="Loading teams..." fullScreen={false} gradient={false} />;

  const q = (search || '').trim().toLowerCase();
  const memosFiltered = !q ? memos : memos.filter((m) =>
    (m.body || '').toLowerCase().includes(q) ||
    (m.owner_name || '').toLowerCase().includes(q) ||
    (m.tags || []).join(' ').toLowerCase().includes(q)
  );
  const highlighted = memosFiltered.filter((m) => m.highlighted).slice(0, 3);
  const rest = memosFiltered.filter((m) => !highlighted.includes(m));
  return (
    <div className="space-y-3" data-testid="memos-team-tab">
      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        {teams.map((t) => (
          <button key={t.id} onClick={() => setActiveId(t.id)} className={cls('flex-shrink-0 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors', activeId === t.id ? 'bg-[#586D52] text-white border-[#586D52]' : 'bg-white dark:bg-[#1a1a1a] text-[#586D52] border-[#586D52]/30 hover:bg-[#586D52]/10')} data-testid={`memo-team-chip-${t.id}`} title={t.name}>
            <UsersIcon className="w-3.5 h-3.5" /> {t.name}
          </button>
        ))}
      </div>
      {/* iter141 — the per-team mini header (name + member count +
          eye icon) was removed. The eye icon now lives in the page
          header (above the tabs). */}
      {!!highlighted.length && (
        <>
          {/* iter141 — Highlighted strip: 3-up grid on every viewport
              (mobile included, per spec). Color matches the unread-
              email card gradient so the visual language stays
              consistent across the app. */}
          <div className="grid grid-cols-3 gap-2" data-testid="memo-team-highlights">
            {highlighted.map((m) => (
              <div key={m.id} id={`memo-${m.id}`} className={cls('p-3 rounded-2xl shadow-sm border bg-gradient-to-r from-[#e8f0e0] via-[#dde8d2] to-[#c9d4b8] border-[#586D52]/40 dark:from-[#243824] dark:via-[#34492f] dark:to-[#2a3a25] dark:border-[#586D52]', focusMemoId === m.id && 'ring-2 ring-[#586D52]')}>
                <div className="flex items-center gap-1 text-[#586D52] dark:text-[#a8c19a] text-[10px] font-bold uppercase tracking-wider mb-1"><Star className="w-3 h-3" /> Highlighted</div>
                <p className="text-xs font-semibold text-[#586D52] dark:text-[#a8c19a] truncate">{m.owner_name}</p>
                <p className="text-[10px] text-muted-foreground">{new Date(m.created_at).toLocaleString()}</p>
                <MemoBody text={m.body} />
              </div>
            ))}
          </div>
          {/* Divider line between highlighted strip and standard memos. */}
          <div className="my-2 border-t border-gray-200 dark:border-gray-700" />
        </>
      )}
      <div className="space-y-2">
        {rest.length === 0 && !highlighted.length && <p className="text-center text-sm text-muted-foreground py-12">{q ? 'No memos match that search.' : 'No memos in this team yet.'}</p>}
        {rest.map((m) => (
          <div key={m.id} id={`memo-${m.id}`} className={cls('p-3 rounded-2xl bg-white dark:bg-[#141414] border border-black/10 dark:border-white/10 shadow-sm', focusMemoId === m.id && 'ring-2 ring-[#586D52]')}>
            <div className="flex items-center justify-between gap-2 mb-1">
              <p className="text-xs font-semibold text-[#586D52]">{m.owner_name}</p>
              <p className="text-[11px] text-muted-foreground">{new Date(m.created_at).toLocaleString()}</p>
            </div>
            <MemoBody text={m.body} />
          </div>
        ))}
      </div>
      {active && <TeamMemoComposer open={composerOpen} onClose={() => setComposerOpen(false)} api={api} team={active} mentionUsers={internalUsers} onPosted={loadMemos} />}
      <CreateTeamPopup open={createTeamOpen} onClose={() => setCreateTeamOpen(false)} api={api} onCreated={loadTeams} />

      {/* Team members modal — name, role, industry, domain. NO email. */}
      <Dialog open={membersOpen} onOpenChange={setMembersOpen}>
        <DialogContent className="bg-white dark:bg-[#1f1f1f] rounded-2xl max-w-md max-h-[80vh] overflow-y-auto" data-testid="memo-team-members-dialog">
          <DialogTitle className="text-base font-semibold">
            {active?.name} · members
          </DialogTitle>
          <div className="space-y-1.5 mt-2">
            {activeMembers.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No members.</p>
            ) : activeMembers.map((u) => (
              <div key={u.id} className="flex items-center gap-3 p-2 rounded-lg bg-muted/30">
                <Avatar className="h-10 w-10 flex-shrink-0">
                  {u.profile_picture ? <AvatarImage src={u.profile_picture} alt="" /> : null}
                  <AvatarFallback className="bg-[#586D52]/20 text-[#586D52] font-semibold text-sm">{initials(u.name)}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold truncate">{u.name}</p>
                  <p className="text-[11px] text-muted-foreground capitalize truncate">
                    {[u.role, u.industry, u.industry_domain].filter(Boolean).join(' · ')}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Render `@[Name](id)` tokens as inline mention pills. Pills MUST be
// `inline` (not `inline-block`) so they participate in normal word
// wrapping — otherwise long names + tight containers (e.g., the
// highlighted strip's 3-up grid on mobile) push the pill off the right
// edge of the card.
const MemoBody = ({ text }) => {
  const parts = useMemo(() => {
    const re = /@\[([^\]]+)\]\(([a-f0-9-]+)\)/g;
    const out = []; let last = 0; let m;
    while ((m = re.exec(text || '')) !== null) {
      if (m.index > last) out.push({ kind: 'text', value: text.slice(last, m.index) });
      out.push({ kind: 'mention', name: m[1], id: m[2] });
      last = re.lastIndex;
    }
    if (last < (text || '').length) out.push({ kind: 'text', value: (text || '').slice(last) });
    return out;
  }, [text]);
  return (
    <p className="text-sm whitespace-pre-wrap break-words">
      {parts.map((p, i) => p.kind === 'mention' ? (
        <span
          key={i}
          className="px-1.5 py-0.5 rounded-full bg-[#586D52]/15 text-[#586D52] text-xs font-semibold mx-0.5 break-all"
        >@{p.name}</span>
      ) : (
        <span key={i}>{p.value}</span>
      ))}
    </p>
  );
};

// ─────────── Invite popup (unchanged from previous iter) ─────────

const InvitePopup = ({ api }) => {
  const [invite, setInvite] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/app-notifications');
        const list = data?.items || (Array.isArray(data) ? data : []);
        const pending = list.find((n) => n.type === 'memo_team_invite' && !n.is_read);
        if (pending) setInvite(pending);
      } catch (e) {/* silent */}
    })();
  }, [api]);
  if (!invite) return null;
  const teamId = invite.data?.team_id;
  const teamName = invite.data?.team_name || 'a team';
  const inviter = invite.data?.inviter_name || 'A teammate';
  const respond = async (accept) => {
    try {
      await api.post(`/memos/teams/${teamId}/respond`, { accept });
      await api.post(`/app-notifications/${invite.id}/read`);
      setInvite(null);
      if (accept) toast.success(`Joined ${teamName}`);
    } catch (e) {/* silent */}
  };
  return (
    <Dialog open={true} onOpenChange={() => {}}>
      <DialogContent className="bg-white dark:bg-[#1f1f1f] rounded-2xl max-w-sm">
        <DialogTitle className="text-base font-semibold">Team memo invite</DialogTitle>
        <p className="text-sm text-muted-foreground">{inviter} invited you to join <span className="font-semibold text-foreground">{teamName}</span>.</p>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" onClick={() => respond(false)} data-testid="memo-invite-reject">Decline</Button>
          <Button onClick={() => respond(true)} className="bg-[#586D52] hover:bg-[#4a5d46]" data-testid="memo-invite-accept">Accept</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ─────────── Page shell ─────────

const MemosPage = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const initialTab = params.get('tab') || 'contacts';
  const focusTeamId = params.get('team');
  const focusMemoId = params.get('memo');
  const [tab, setTab] = useState(initialTab);
  // Cross-tab search query — page-level so switching tabs preserves it.
  const [search, setSearch] = useState('');
  // Composer popup state lives at the page level — the header `+`
  // buttons (which are scoped to MemosPage) need to drive them
  // directly. Tabs only consume open/close + refresh.
  const [personalOpen, setPersonalOpen] = useState(false);
  const [personalEditing, setPersonalEditing] = useState(null);
  const [teamOpen, setTeamOpen] = useState(false);
  const [createTeamOpen, setCreateTeamOpen] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const bumpRefresh = useCallback(() => setRefreshTick((x) => x + 1), []);
  // Contact-tab composer state lives at page level so the back-button
  // (Memo button inside the thread header) can open it via callback.
  const [contactComposer, setContactComposer] = useState({ open: false, contact: null, onPosted: null });

  // iter126.1 — Hard-scroll the main content area to top on every
  // tab switch. Radix's Tabs trigger steals focus to the new tab
  // which then auto-scrolls into view (the source of the user-
  // reported "page starts slightly down from the top" bug). Run as
  // a layout effect + blur the focused trigger to prevent
  // browsers' auto-scroll-into-view from firing post-paint.
  const contentRef = useRef(null);
  useLayoutEffect(() => {
    if (contentRef.current) contentRef.current.scrollTop = 0;
    window.scrollTo(0, 0);
    // Defer a second scroll to win against Radix's focus restore.
    const t = setTimeout(() => {
      if (document.activeElement && document.activeElement.blur) {
        document.activeElement.blur();
      }
      window.scrollTo(0, 0);
    }, 0);
    return () => clearTimeout(t);
  }, [tab]);

  // Auto-jump to a specific memo if the URL says so (notification click).
  useEffect(() => {
    if (!focusMemoId) return;
    const t = setTimeout(() => {
      const el = document.getElementById(`memo-${focusMemoId}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 600);
    return () => clearTimeout(t);
  }, [focusMemoId, tab]);

  const headerRight = useMemo(() => {
    if (tab === 'personal') {
      return (
        <button
          onClick={() => { setPersonalEditing(null); setPersonalOpen(true); }}
          className="w-10 h-10 rounded-full hover:bg-muted flex items-center justify-center"
          aria-label="New memo"
          data-testid="memos-header-plus"
        >
          <Plus className="w-5 h-5" />
        </button>
      );
    }
    if (tab === 'team') {
      return (
        <div className="flex items-center gap-1">
          {user?.role === 'admin' && (
            <button
              onClick={() => navigate('/admin/memos')}
              className="w-10 h-10 rounded-full hover:bg-muted flex items-center justify-center"
              aria-label="Admin: all memos"
              title="Admin · all memos"
              data-testid="memos-header-admin"
            >
              <Globe className="w-5 h-5" />
            </button>
          )}
          {/* Eye icon — opens the active-team member roster modal.
              Wired via a window event because the active team lives
              inside the TeamMemosTab component subtree. */}
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('memos:open-team-members'))}
            className="w-10 h-10 rounded-full hover:bg-muted flex items-center justify-center"
            aria-label="View team members"
            data-testid="memos-header-eye"
          >
            <Eye className="w-5 h-5" />
          </button>
          <button
            onClick={() => setTeamOpen(true)}
            className="w-10 h-10 rounded-full hover:bg-muted flex items-center justify-center"
            aria-label="New memo"
            data-testid="memos-header-plus"
          >
            <Plus className="w-5 h-5" />
          </button>
          <button
            onClick={() => setCreateTeamOpen(true)}
            className="w-10 h-10 rounded-full hover:bg-muted flex items-center justify-center"
            aria-label="New team"
            data-testid="memos-header-new-team"
          >
            <UserPlus className="w-5 h-5" />
          </button>
        </div>
      );
    }
    // Contact/Personal tabs — show the admin icon if applicable so it's
    // reachable from any tab.
    if (user?.role === 'admin') {
      return (
        <button
          onClick={() => navigate('/admin/memos')}
          className="w-10 h-10 rounded-full hover:bg-muted flex items-center justify-center"
          aria-label="Admin: all memos"
          title="Admin · all memos"
          data-testid="memos-header-admin"
        >
          <Globe className="w-5 h-5" />
        </button>
      );
    }
    return null;
  }, [tab, navigate, user]);

  usePageHeader({ title: 'Memos', rightSlot: headerRight }, [headerRight]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000]" data-testid="memos-page">
      <div ref={contentRef} className="max-w-4xl mx-auto p-4 space-y-4">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="contacts" data-testid="memos-tab-contacts">Contact Memos</TabsTrigger>
            <TabsTrigger value="personal" data-testid="memos-tab-personal">Personal Memos</TabsTrigger>
            <TabsTrigger value="team" data-testid="memos-tab-team">Team Memos</TabsTrigger>
          </TabsList>
          {/* Cross-tab search — filters whichever tab is active by
              contact name, tags, memo title/body, owner name, etc. */}
          <div className="relative mt-3">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search memos…"
              className="pl-9 bg-white/70 dark:bg-[#141414]/70"
              data-testid="memos-search-input"
            />
          </div>
          <TabsContent value="contacts" className="mt-4">
            <ContactMemosTab api={api} openComposerForContact={(c, onPosted) => setContactComposer({ open: true, contact: c, onPosted })} search={search} />
          </TabsContent>
          <TabsContent value="personal" className="mt-4">
            <PersonalMemosTab
              api={api}
              composerOpen={personalOpen}
              setComposerOpen={setPersonalOpen}
              editing={personalEditing}
              setEditing={setPersonalEditing}
              refreshKey={refreshTick}
              search={search}
            />
          </TabsContent>
          <TabsContent value="team" className="mt-4">
            <TeamMemosTab
              api={api}
              composerOpen={teamOpen}
              setComposerOpen={setTeamOpen}
              createTeamOpen={createTeamOpen}
              setCreateTeamOpen={setCreateTeamOpen}
              focusTeamId={focusTeamId}
              focusMemoId={focusMemoId}
              refreshKey={refreshTick}
              search={search}
            />
          </TabsContent>
        </Tabs>
        <ContactMemoComposer
          open={contactComposer.open}
          onClose={() => setContactComposer({ open: false, contact: null, onPosted: null })}
          contact={contactComposer.contact}
          api={api}
          onPosted={contactComposer.onPosted}
        />
        <InvitePopup api={api} />
      </div>
    </div>
  );
};

export default MemosPage;
